import React, { useId } from 'react'

interface GeoNexoraLogoProps {
  /** Height of the mark in px (the mark is 110 × 130, so width = size × 0.846). */
  size?: number
  className?: string
  /**
   * 'mark'  – the standing-pin G only (default)
   * 'full'  – mark + "Geonexora" wordmark
   * 'tile'  – mark on the navy rounded-square app tile
   */
  variant?: 'mark' | 'full' | 'tile'
  /**
   * Colour of the G. Defaults to `currentColor`, so the mark follows the
   * surrounding text colour and therefore the light/dark theme.
   */
  ink?: string
}

/** Brand blue of the location square. */
export const GEONEXORA_BLUE = '#2F6BFF'
/** Brand navy used for the app tile and dark grounds. */
export const GEONEXORA_NAVY = '#0B1A33'

/**
 * The G silhouette: a half-disc on the left with a square bottom-right corner,
 * a 27-unit counter cut out, and the blue location square in the opening.
 * Rotated 45° the sharp corner becomes the pin's tip.
 */
const Mark: React.FC<{ ink: string; maskId: string }> = ({ ink, maskId }) => (
  <>
    <defs>
      <mask id={maskId}>
        <rect x="-30" y="-30" width="170" height="190" fill="#fff" />
        <circle cx="50" cy="50" r="27" fill="#000" />
      </mask>
    </defs>
    <g transform="rotate(45 50 50)">
      <path
        d="M50 0 A50 50 0 0 0 0 50 A50 50 0 0 0 50 100 L100 100 L100 50 L50 50 Z"
        fill={ink}
        mask={`url(#${maskId})`}
      />
      <rect x="55" y="23" width="22" height="22" fill={GEONEXORA_BLUE} />
    </g>
  </>
)

/** GeoNexora logo — "standing pin" G (option 9, variant C). */
export const GeoNexoraLogo: React.FC<GeoNexoraLogoProps> = ({
  size = 28,
  className = '',
  variant = 'mark',
  ink = 'currentColor',
}) => {
  // Each instance needs its own mask id: several logos can share a page.
  const maskId = `gn-mask-${useId().replace(/:/g, '')}`

  if (variant === 'tile') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={className}
        role="img"
        aria-label="GeoNexora"
      >
        <rect width="100" height="100" rx="22" fill={GEONEXORA_NAVY} />
        <svg x="21.75" y="15.8" width="56.5" height="68.4" viewBox="0 0 100 121">
          <Mark ink="#FFFFFF" maskId={maskId} />
        </svg>
      </svg>
    )
  }

  const mark = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-5 -5 110 130"
      width={Math.round((size * 110) / 130)}
      height={size}
      className={variant === 'mark' ? className : undefined}
      role="img"
      aria-label="GeoNexora"
    >
      <Mark ink={ink} maskId={maskId} />
    </svg>
  )

  if (variant === 'mark') return mark

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.24, color: ink }}
    >
      {mark}
      <span
        style={{
          fontFamily: "'Inter Tight', 'Inter', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: size * 0.72,
          letterSpacing: `${-0.042 * size * 0.72}px`,
          lineHeight: 1,
        }}
      >
        Geonexora
      </span>
    </span>
  )
}

export default GeoNexoraLogo
